import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { UnitsProvider } from "@/hooks/useUnits";
import ProtectedRoute from "@/components/ProtectedRoute";
import NoIndexRobots from "@/components/NoIndexRobots";
import Layout from "@/components/Layout";
import PublicLayout from "@/components/PublicLayout";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import ViewportDebugPanel from "@/components/ViewportDebugPanel";

// Eager: landing/auth (LCP path)
import AuthPage from "./pages/Auth";

// Lazy: split per route to reduce initial JS
const Landing = lazy(() => import("./pages/Landing.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const MatchesPage = lazy(() => import("./pages/Matches"));
const AddMatch = lazy(() => import("./pages/AddMatch"));
const EditMatch = lazy(() => import("./pages/EditMatch"));
const DirectInviteMatch = lazy(() => import("./pages/DirectInviteMatch"));
const Stats = lazy(() => import("./pages/Stats"));
const Rankings = lazy(() => import("./pages/Rankings"));
const InsightsPage = lazy(() => import("./pages/Insights"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Refund = lazy(() => import("./pages/Refund"));
const Help = lazy(() => import("./pages/Help"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Settings = lazy(() => import("./pages/Settings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Admin = lazy(() => import("./pages/Admin"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Faq = lazy(() => import("./pages/Faq"));
const Features = lazy(() => import("./pages/Features"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const TennisStatisticsGuide = lazy(() => import("./pages/TennisStatisticsGuide"));
const Demo = lazy(() => import("./pages/Demo"));
const TryMatch = lazy(() => import("./pages/TryMatch"));
const Academy = lazy(() => import("./pages/Academy"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AcademyCategory = lazy(() => import("./pages/AcademyCategory"));
const AcademyArticle = lazy(() => import("./pages/AcademyArticle"));
const InviteMatch = lazy(() => import("./pages/InviteMatch"));

const queryClient = new QueryClient();

const RedirectEdit = () => {
  const { id } = useParams();
  return <Navigate to={`/app/edit/${id}`} replace />;
};

const AppShell = () => (
  <ProtectedRoute>
    <Layout>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </Layout>
  </ProtectedRoute>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <UnitsProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/auth" element={<NoIndexRobots><AuthPage /></NoIndexRobots>} />
              <Route path="/.lovable/oauth/consent" element={<NoIndexRobots><OAuthConsent /></NoIndexRobots>} />
              <Route path="/reset-password" element={<NoIndexRobots><ResetPasswordPage /></NoIndexRobots>} />
              <Route path="/" element={<Landing />} />
              <Route path="/try" element={<NoIndexRobots><TryMatch /></NoIndexRobots>} />
              <Route path="/invite" element={<NoIndexRobots><PublicLayout><InviteMatch /></PublicLayout></NoIndexRobots>} />

              {/* Authenticated app shell — /app/* */}
              <Route path="/app" element={<NoIndexRobots><AppShell /></NoIndexRobots>}>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="matches" element={<MatchesPage />} />
                <Route path="invitations/:id" element={<DirectInviteMatch />} />
                <Route path="add" element={<AddMatch />} />
                <Route path="edit/:id" element={<EditMatch />} />
                <Route path="stats" element={<Stats />} />
                <Route path="rankings" element={<Rankings />} />
                <Route path="insights" element={<InsightsPage />} />
                <Route path="settings" element={<Settings />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="admin" element={<Admin />} />
              </Route>

              {/* Backwards-compatible permanent redirects for old app routes */}
              <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
              <Route path="/matches" element={<Navigate to="/app/matches" replace />} />
              <Route path="/add" element={<Navigate to="/app/add" replace />} />
              <Route path="/edit/:id" element={<RedirectEdit />} />
              <Route path="/stats" element={<Navigate to="/app/stats" replace />} />
              <Route path="/rankings" element={<Navigate to="/app/rankings" replace />} />
              <Route path="/insights" element={<Navigate to="/app/insights" replace />} />
              <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
              <Route path="/admin" element={<Navigate to="/app/admin" replace />} />

              {/* Public marketing/content pages */}
              <Route path="/features" element={<Features />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/support" element={<Navigate to="/help" replace />} />
              <Route path="/tennis-statistics-guide" element={<TennisStatisticsGuide />} />
              <Route path="/academy" element={<Academy />} />
              <Route path="/academy/:category" element={<AcademyCategory />} />
              <Route path="/academy/:category/:slug" element={<AcademyArticle />} />
              <Route path="/preview" element={<Navigate to="/demo" replace />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
              <Route path="/refund" element={<PublicLayout><Refund /></PublicLayout>} />
              <Route path="/help" element={<PublicLayout><Help /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
              <Route path="/unsubscribe" element={<NoIndexRobots><PublicLayout><Unsubscribe /></PublicLayout></NoIndexRobots>} />
              <Route path="/delete-account" element={<PublicLayout><DeleteAccount /></PublicLayout>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <ViewportDebugPanel />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </UnitsProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
